import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

// Components
import {
  Element,
  Color,
  Size,
  Align,
  Icon,
  Carousel
} from '@americanexpress/aqx-components';
import { StackedTile } from './StackedTile';
import replaceStringWithVars from 'src/universal/utils/replaceStringWithVars';

// selectors
import { getStackedCarouselSettings } from 'page_modules/business-pdp/redux';

// Utils
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';
import { sendDTMClickEvent } from 'service_modules/models/dtm-tagging';

import styles from '../__styles__/index.scss?modules';

const { Container, Prev, Next, Dots, Item, MultiFrameItems } = Carousel;

const fireDtmEvent = dtmObj => {
  setTimeout(() => {
    sendDTMClickEvent(dtmObj);
  }, 500);
};

const PaddleIcon = ({ isLeft }) => (
  <Icon
    directory="dls"
    imageName={isLeft ? 'dls-icon-left' : 'dls-icon-right'}
    $size={Size.SM}
    hiddenToScreenReaders="true"
    theme={{
      paddingRight: 0,
      paddingLeft: 0,
      color: Color.BrightBlue
    }}
  />
);

const StackedCarousel = ({ contentList, carouselSettings }) => {
  const {
    triggerAutoSlidingOnPageLoad,
    intervalBetweenSlidings,
    slidingSpeed,
    ariaLabelPause,
    ariaLabelPlay
  } = carouselSettings;

  const intervalId = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [shouldAutoSlide, setShouldAutoSlide] = useState(
    triggerAutoSlidingOnPageLoad
  );

  const dtmEventHash = parseArrayToObject(carouselSettings?.dtmEvents || [], 'key');

  const handleNavAction = (state, dtmKey, direction) => {
    setShouldAutoSlide(false);

    const indexNew = state?.currentIndex;

    if (activeIndex !== indexNew) {
      setActiveIndex(indexNew);
    }

    const dtmObj = dtmEventHash[dtmKey];
    const dotClickIndexDtm = {
      ...dtmObj,
      eventAsset: replaceStringWithVars({
        varsObject: {
          index: state?.currentIndex + 1
        },
        string: dtmObj.eventAsset
      })
    };

    if (dtmObj) {
      let assetVal;
      if (dtmKey === 'paddleClick') {
        assetVal = `${direction}_${dtmObj.eventAsset}`;
      } else if (dtmKey === 'dotsClick') {
        assetVal = dotClickIndexDtm.eventAsset;
      } else {
        assetVal = `${dtmObj.eventAsset}_index${indexNew}`;
      }

      fireDtmEvent({ ...dtmObj, eventAsset: assetVal });
    }
  };

  const toggleAutoPlay = () => {
    const targetMode = !shouldAutoSlide;
    setShouldAutoSlide(targetMode);

    const dtmObj = dtmEventHash.pauseStartAutoRotation;

    if (dtmObj) {
      fireDtmEvent({
        ...dtmObj,
        eventAsset: `${targetMode ? 'play' : 'pause'}_${dtmObj.eventAsset}`
      });
    }
  };

  useEffect(
    () => {
      if (shouldAutoSlide) {
        intervalId.current = setInterval(() => {
          setActiveIndex(prevIndex => {
            if (prevIndex === contentList.length - 1) {
              return 0;
            }
            return prevIndex + 1;
          });
        }, intervalBetweenSlidings);
      } else {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }

      return () => {
        clearInterval(intervalId.current);
        intervalId.current = null;
      };
    },
    [shouldAutoSlide, intervalBetweenSlidings, contentList.length]
  );

  return (
    contentList && (
      <Container totalItems={contentList?.length} speed={slidingSpeed}>
        <MultiFrameItems
          onSwipe={state => {
            handleNavAction(state, 'swipeAction');
          }}
          fullWidthMode={true}
          injectedIndex={activeIndex}
        >
          {contentList.map((item, index) => {
            const isInactive = index !== activeIndex;
            return (
              <Item
                className={
                  isInactive ? styles.contentInactive : styles.contentActive
                }
                key={index}
                index={index}
              >
                <StackedTile content={item} />
              </Item>
            );
          })}
        </MultiFrameItems>
        <Element className={styles.floatingRow}>
          <Element className={styles.controlWrap}>
            <Prev
              className={styles.paddleLeft}
              onClick={state => {
                handleNavAction(state, 'paddleClick', 'left');
              }}
            >
              <PaddleIcon isLeft={true} />
            </Prev>
            <Element
              className={styles.dotsWrap}
              theme={{
                margin: 0,
                padding: 0,
                textAlign: Align.Center
              }}
            >
              <Dots
                inactive={
                  <Element
                    Component="span"
                    className={styles.dot}
                    theme={{
                      marginLeft: 0,
                      marginRight: 0,
                      bgColor: Color.Gray03
                    }}
                  />
                }
                active={
                  <Element
                    Component="span"
                    className={styles.dot}
                    theme={{
                      marginLeft: 0,
                      marginRight: 0,
                      bgColor: Color.BrightBlue
                    }}
                  />
                }
                clickAble={true}
                onClick={state => {
                  handleNavAction(state, 'dotsClick');
                }}
              />
            </Element>
            <Next
              className={styles.paddleRight}
              onClick={state => {
                handleNavAction(state, 'paddleClick', 'right');
              }}
            >
              <PaddleIcon isLeft={false} />
            </Next>
          </Element>
          <Element
            Component="button"
            aria-label={shouldAutoSlide ? ariaLabelPause : ariaLabelPlay}
            onClick={toggleAutoPlay}
            className={styles.stopPlayCta}
          >
            <Icon
              directory="dls"
              imageName={
                shouldAutoSlide ? 'dls-icon-pause-filled' : 'dls-icon-play-filled'
              }
              $size={Size.SM}
              hiddenToScreenReaders="true"
              theme={{
                paddingRight: 0,
                paddingLeft: 0,
                color: Color.White
              }}
            />
          </Element>
        </Element>
      </Container>
    )
  );
};

const mapStateToProps = state => ({
  carouselSettings: getStackedCarouselSettings(state)
});

export default connect(mapStateToProps)(React.memo(StackedCarousel));
